import { Injectable, OnDestroy } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';

import { fuseConfig } from 'app/fuse-config/index';
import { FuseConfig } from '@fuse/types';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { User } from '@fuse/types/user';
import { AuthService as SocialAuthService, GoogleLoginProvider, FacebookLoginProvider } from 'angularx-social-login';

import decode from 'jwt-decode';
import { Router } from '@angular/router';

import * as _moment from 'moment';


const moment = _moment;

const jwtHelper = new JwtHelperService();

@Injectable()

export class AuthService implements OnDestroy {
    private _unsubscribeAll: Subject<any>;
    private _fuseConfig: FuseConfig;

    private refreshTokenInProgress = false;
    private refreshTokenTimer = 10;

    public authenticationError: any;
    public authenticationComplete: Subject<boolean> = new Subject();
    public userSetComplete: Subject<boolean> = new Subject();
    public logoutComplete: Subject<boolean> = new Subject();
    public passwordResetComplete: Subject<boolean> = new Subject();
    public socialLoginResponse: Subject<any> = new Subject();
    public processedEmail = { email: '', action: '' };
    public userInfo: User;
    public errorCode = 0;

    public registeredWithSocial = '';
    public authWithSocial = false;

    constructor(
        private _router: Router,
        public _http: HttpClient,
        private socialAuthService: SocialAuthService
    ) {
        this._fuseConfig = fuseConfig;
        this._unsubscribeAll = new Subject();
    }

    /**
     * Private functions
     */

    protected setToken(token): void {
        localStorage.setItem('token', token);
    }

    private getToken(): string {
        const token = localStorage.getItem('token');
        return token;
    }

    /**
     * Public methods
     */

    public isAuthenticated(): any {
        let token = this.getToken();

        try {
            if (jwtHelper.isTokenExpired(token)) {
                console.log('Token expired!');
                token = '';
                this.setToken(token);
            }
        } catch (error) {
            console.log('Error with previous token. Resetting it.');
            token = '';
            this.setToken(token);
        }

        return token !== '' ? token : false;
    }

    public authenticate(email, password): void {
        const params = {
            email: email,
            password: password
        };

        this.authenticationError = null;
        this._http.post(this._fuseConfig.api.url + '/auth/employee/login', params)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(
                response => {
                    console.log(response);
                    if (response['token']) {
                        this.setToken(response['token']);
                        this.setUser();
                        this.authenticationComplete.next(true);
                        this.authWithSocial = false;
                    }
                },
                err => {
                    this.authenticationError = err['statusText'];
                    this.authenticationError = 'o_err_' + this.authenticationError;
                    this.authenticationError = this.authenticationError.toString();
                    this.authenticationComplete.next(false);
                    params['password'] = null;
                    params['email'] = null;
                }
            );
    }

    public authenticateSocial(source: string, doLogin: boolean = true): void {

        let provider = FacebookLoginProvider.PROVIDER_ID;
        if (source === 'google') {
            provider = GoogleLoginProvider.PROVIDER_ID;
        }
        this.socialAuthService.signIn(provider)
            .then(data => {
                console.log(data);

                this.authWithSocial = false;

                if (doLogin) {
                    const params = {
                        token: data.authToken,
                        source: source
                    };

                    this._http.post(this._fuseConfig.api.url + '/auth/employee/login-with-social-account', params)
                        .pipe(takeUntil(this._unsubscribeAll))
                        .subscribe(
                            response => {
                                if (response['token']) {
                                    this.setToken(response['token']);
                                    this.setUser();
                                    this.authenticationComplete.next(true);
                                    this.authWithSocial = true;
                                }
                            },
                            err => {
                                this.authenticationError = err['statusText'];
                                this.authenticationError = 'o_err_' + this.authenticationError;
                                this.authenticationError = this.authenticationError.toString();
                                console.log('Social login API error!');
                                console.log(err);

                                this.authenticationComplete.next(false);
                                params['password'] = null;
                                params['email'] = null;
                            }
                        );
                }

                this.socialLoginResponse.next(data);

            })
            .catch(err => {
                console.log('Social login error!');
                console.log(err);

                this.socialLoginResponse.next(null);
            });
    }

    public updatePreferredLanguage(preferredLang: string): Promise<any> {

        const params = {
            preferred_lang: preferredLang
        };

        return new Promise((resolve, reject) => {
            this._http.put(this._fuseConfig.api.url + '/portal/member/update', params)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    response => {
                        resolve(response);
                    }, reject);
        });
    }

    public setUser(): any {
        console.log('Set user called.');
        const token = this.getToken();
        if (token && token !== '') {
            if (!this.userInfo) {
                this._http.get(this._fuseConfig.api.url + '/portal/member/me')
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(
                        response => {
                            this.userInfo = response['data'];
                            localStorage.setItem('user', JSON.stringify(this.userInfo));
                            this.userSetComplete.next(true);
                        },
                        err => {
                            this.userInfo = null;
                            localStorage.removeItem('user');
                            this.userSetComplete.next(false);
                            console.log('error:', err);
                        }
                    );
            }
        } else {
            this.userInfo = null;
            localStorage.removeItem('user');
        }
    }

    public getUser(): any {
        const user = JSON.parse(localStorage.getItem('user'));
        return user;
    }

    public logout(): void {
        this._http.get(this._fuseConfig.api.url + '/auth/employee/logout')
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(
                response => {
                    console.log(response);
                    localStorage.removeItem('token');
                    this.setToken('');
                    this.setUser();
                    if (this.authWithSocial) {
                        this.socialAuthService.signOut();
                        this.authWithSocial = false;
                    }
                    this.logoutComplete.next(true);
                },
                err => {
                    localStorage.removeItem('token');
                    this.setToken('');
                    this.setUser();
                    console.log('error:', err);
                    if (this.authWithSocial) {
                        this.socialAuthService.signOut();
                        this.authWithSocial = false;
                    }
                    this.logoutComplete.next(false);
                }
            );
    }

    public resetPassword(email: string): void {
        const params = {
            email: email
        };

        this._http.post(this._fuseConfig.api.url + '/auth/member/forgot-password', params)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(
                response => {
                    console.log(response);
                    this.processedEmail.email = email;
                    this.processedEmail.action = 'reset';
                    this.passwordResetComplete.next(true);
                },
                err => {
                    this.processedEmail.email = '';
                    this.processedEmail.action = '';
                    console.log('error:', err);
                    this.passwordResetComplete.next(err);
                }
            );
    }

    private callRefreshToken(): void {
        this.refreshTokenInProgress = true;
        this._http.get(this._fuseConfig.api.url + '/auth/employee/refresh')
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(
                response => {
                    if (response['token']) {
                        this.setToken(response['token']);
                        this.setUser();
                        this.authenticationComplete.next(true);
                    }
                    this.refreshTokenInProgress = false;

                    console.log('Token refreshed');
                },
                err => {
                    this.authenticationError = err['statusText'];
                    this.authenticationError = 'o_err_' + this.authenticationError;
                    this.authenticationError = this.authenticationError.toString();
                    this.authenticationComplete.next(false);

                    this.refreshTokenInProgress = false;
                }
            );
    }

    public refreshToken(): void {
        const token = this.isAuthenticated();

        if (token) {
            const tokenExpDate = jwtHelper.getTokenExpirationDate(token);
            const currDate = moment();
            const expDate = moment(tokenExpDate);
            const diff = expDate.diff(currDate, 'minutes');

            console.log((diff) + ' min. to refresh');

            if (diff < this.refreshTokenTimer && !this.refreshTokenInProgress) {
                this.callRefreshToken();
            }
        }
    }

    /**
     * Lifecycle hooks
     */

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }
}
